import React, { useEffect, useRef, useState } from 'react'

import { Box, Button, Typography } from '@mui/material'

/**
 * How long the reveal may take to START before it is abandoned (ms).
 *
 * 2500. This is the "never blocks" guarantee in AC6 made concrete, and the cases it
 * covers are all real: a browser that silently refuses autoplay despite `muted`, a 404
 * on the media, a decoder that will not take the file, a stalled first byte on cellular.
 * None of them fire an `error` event reliably, so a timer is the only thing that catches
 * them.
 *
 * Why not shorter: the poster is already on screen within this window, so the viewer is
 * looking at the reveal's opening image rather than at nothing, and cutting at 1s would
 * abandon reveals that were about to play on a slow connection — the exact audience the
 * feature is for.
 *
 * Why not longer: past about two and a half seconds the viewer has been held on a still
 * image with no explanation, which is indistinguishable from a broken page.
 */
const START_TIMEOUT_MS = 2500

/**
 * A hard ceiling on the whole reveal (ms).
 *
 * The server caps uploads at 6s, so 9s is that plus the start window plus room for a
 * slow decode. It exists for the case `ended` NEVER FIRES — a video that begins playing
 * and then stalls mid-buffer, which on a city street on cellular is not an edge case. A
 * stalled video fires no `error` and no `ended`; without this ceiling the viewer sits on
 * a frozen frame until they think to tap, having tapped a physical object expecting their
 * certificate.
 *
 * This is the single most important number in the file. Everything else is a nicety.
 */
const HARD_CEILING_MS = 9000

/** The fade from the reveal into the certificate (ms). */
const FADE_MS = 420

export interface RevealScreenProps {
  videoUrl: string
  posterUrl: string
  /**
   * Called exactly once, when the reveal is finished with the screen for any reason —
   * played out, skipped, failed, or timed out. The caller does not get to know which,
   * deliberately: every one of them means "show the certificate now", and giving the
   * caller a reason would invite it to treat one of them as an error.
   */
  onDone: () => void
  /**
   * Called when the viewer actually SAW the reveal — it played to its end, or they
   * skipped it, which both mean they have made their decision about this certificate's
   * reveal. NOT called on a failure or a timeout: a reveal nobody saw has not been seen,
   * and recording it would mean one unlucky load permanently cost them the moment.
   */
  onSeen: () => void
}

/**
 * VRG-790 — the reveal: a short video played full-screen after a tag verifies, before
 * the certificate renders.
 *
 * ## This component cannot trap anyone, and that is its entire design constraint
 *
 * It is the first thing a person sees after confirming that the object in their hands is
 * real, which makes it the highest-attention surface in the product — and it stands
 * between them and the certificate they just verified. So there are FOUR independent
 * ways out, and they do not share a mechanism:
 *
 *   1. `ended` — the normal path.
 *   2. A tap anywhere on the screen, at any time, including after the video ends.
 *   3. `onError` — a 404, a codec the device refuses.
 *   4. Two timers — one for "never started", one hard ceiling for "started and stalled".
 *
 * Any one of them is sufficient. That redundancy is the point: a video element has
 * several failure modes that fire no event at all, so an implementation that trusted
 * events would strand a viewer on a black screen in the street.
 *
 * ## Why timers own the exits rather than events
 *
 * `scanLoader.ts` records this lesson for the scan cover and it applies identically: a
 * backgrounded tab freezes the document timeline, so `onfinish`/`transitionend` may never
 * fire while `setTimeout` still will. So the fade is a CSS transition for looks, and the
 * `onDone` that actually reveals the certificate is armed with `setTimeout` in the same
 * statement that starts it. The difference is a screen that clears and a page bricked
 * behind a black sheet.
 *
 * (The same applies in the Claude preview pane, where `requestAnimationFrame` and
 * `ResizeObserver` do not fire — so nothing here depends on either.)
 *
 * ## `muted` + `playsInline` + `autoPlay` are all three required
 *
 * Without `muted`, iOS Safari refuses to autoplay outright. Without `playsInline`, some
 * iOS versions take the video FULLSCREEN in the native player, which tears the viewer
 * out of the page entirely and gives them a system "Done" button instead of the
 * certificate. The existing `VideoPlayer.tsx` in this directory sets neither (it has
 * `webkit-playsinline`, which React does not forward, and no `muted` at all) — it gets
 * away with it only because it is user-triggered with controls.
 *
 * A reveal has no audio track anyone needs, so `muted` costs nothing here.
 */
export const RevealScreen = ({
  videoUrl,
  posterUrl,
  onDone,
  onSeen,
}: RevealScreenProps) => {
  const [isFading, setIsFading] = useState(false)

  /*
    Every exit routes through this, and it is idempotent. Four independent paths can fire
    at once — a tap during a stall that is also hitting the ceiling — and `onDone` must
    reach the caller exactly once or the certificate page would re-run its transition.
  */
  const finishedRef = useRef(false)

  /** Set by the first `playing`/`timeupdate`. Distinguishes "never started" (abandon
   *  silently) from "started and stalled" (the hard ceiling's job). */
  const startedRef = useRef(false)

  const finish = (seen: boolean) => {
    if (finishedRef.current) return
    finishedRef.current = true

    if (seen) onSeen()

    setIsFading(true)
    /*
      The fade is a nicety; the HAND-OFF is not. So `onDone` is owned by this timer and
      never by `transitionend` — see the docblock. If the tab is backgrounded mid-fade
      the transition never completes and this still fires.
    */
    window.setTimeout(onDone, FADE_MS)
  }

  useEffect(() => {
    /*
      Two timers, not one, because they catch different failures and a single timer cannot
      do both: a short one would cut a playing video off mid-reveal, and a long one would
      hold a viewer on a still image for nine seconds when the video was never going to
      play at all.
    */
    const startTimer = window.setTimeout(() => {
      // `started` is set by the first `playing`/`timeupdate`. Still false here means the
      // video never got going, so there is nothing to have been seen.
      if (!startedRef.current) finish(false)
    }, START_TIMEOUT_MS)

    const ceilingTimer = window.setTimeout(() => finish(false), HARD_CEILING_MS)

    return () => {
      window.clearTimeout(startTimer)
      window.clearTimeout(ceilingTimer)
    }
    // Mount-only: both timers are absolute from the moment the reveal appears, and
    // re-arming them on a re-render would let a re-rendering page postpone the ceiling
    // indefinitely — which is the one thing the ceiling exists to prevent.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleStarted = () => {
    startedRef.current = true
  }

  return (
    <Box
      // The whole screen is the skip target (§AC7). A dedicated "skip" button would be
      // smaller, would need placing over artwork nobody has seen yet, and would read as
      // chrome on top of the brand's moment.
      onClick={() => finish(true)}
      role="button"
      tabIndex={0}
      aria-label="Skip and view the certificate"
      onKeyDown={event => {
        if (event.key === 'Enter' || event.key === ' ') finish(true)
      }}
      sx={{
        position: 'fixed',
        inset: 0,
        // Above everything, including the app's own sticky chrome and any drawer.
        zIndex: theme => theme.zIndex.modal + 10,
        // Black, not the theme background: a reveal is letterboxed artwork and a brand's
        // own backdrop behind it would frame it as a page element rather than a moment.
        backgroundColor: '#000',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        opacity: isFading ? 0 : 1,
        transition: `opacity ${FADE_MS}ms ease-out`,
        // Honoured even though the caller already refuses to mount this under reduced
        // motion: belt and braces costs one line, and a future caller may forget.
        '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
      }}
    >
      <Box
        component="video"
        src={videoUrl}
        /*
          The poster is REQUIRED by the API for exactly this attribute. Without it the
          element paints its own black background until the first frame decodes, which at
          this moment reads as a broken page rather than as a beat.
        */
        poster={posterUrl}
        // All three are load-bearing — see the docblock. `muted` must also be set as a
        // property on some iOS versions, which `onCanPlay` below does.
        muted
        playsInline
        autoPlay
        // Not `metadata`: this video is the thing the viewer is waiting for, so there is
        // nothing to be gained by fetching it lazily. The 5MB cap is what bounds it.
        preload="auto"
        // No controls, ever. The viewer is not operating a player; a scrub bar over a
        // brand's reveal would be the loudest element on the screen.
        controls={false}
        onPlaying={handleStarted}
        onTimeUpdate={handleStarted}
        onEnded={() => finish(true)}
        // A 404, a codec refusal, a truncated file. Straight to the certificate with
        // nothing said about it — a failed reveal is never an error state (AC6).
        onError={() => finish(false)}
        onCanPlay={event => {
          /*
            Some iOS Safari versions honour the muted PROPERTY but not the attribute, and
            an unmuted autoplay is refused outright. Setting it here as well is the
            cheapest way to cover that, and it is idempotent.
          */
          const video = event.currentTarget as HTMLVideoElement
          video.muted = true
          /*
            `autoPlay` can still be refused. The returned promise rejects, and without
            this the reveal would sit on its poster until START_TIMEOUT_MS — correct, but
            2.5s slower than it needs to be. The catch is empty on purpose: the timer is
            the real guarantee and a refusal is not an error worth reporting.
          */
          void video.play().catch(() => undefined)
        }}
        sx={{
          // `contain`, not `cover`: a reveal is composed artwork and cropping it would
          // cut whatever the brand framed at the edges.
          maxWidth: '100%',
          maxHeight: '100%',
          width: 'auto',
          height: 'auto',
          display: 'block',
        }}
      />

      {/*
        Appears only once the video has run out, and it is an affordance rather than the
        only way forward — the whole screen is still tappable, so nobody can be stuck
        waiting for this button to be noticed.
      */}
      <RevealDoneButton onClick={() => finish(true)} />
    </Box>
  )
}

/**
 * The "View certificate" affordance, held back until the reveal is over.
 *
 * Its own component so its visibility state does not live on `RevealScreen` and cause
 * the parent (and therefore the `<video>`) to re-render when it appears. A re-render that
 * remounted the video element would restart the reveal, which is the kind of bug that
 * only shows up on a slow device.
 */
const RevealDoneButton = ({ onClick }: { onClick: () => void }) => {
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    /*
      Shown on a TIMER rather than on the video's `ended` event, so it appears even for a
      reveal that stalls — a viewer looking at a frozen frame needs the way out more than
      one watching a reveal play properly, not less. Sits comfortably inside
      HARD_CEILING_MS so it is on screen before the ceiling fires.
    */
    const timer = window.setTimeout(() => setIsVisible(true), 1800)
    return () => window.clearTimeout(timer)
  }, [])

  return (
    <Box
      sx={{
        position: 'absolute',
        bottom: 'calc(env(safe-area-inset-bottom, 0px) + 32px)',
        left: 0,
        right: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 1.5,
        // Fades in rather than popping, and `pointerEvents: none` while hidden so an
        // invisible button cannot swallow the tap-anywhere skip behind it.
        opacity: isVisible ? 1 : 0,
        pointerEvents: isVisible ? 'auto' : 'none',
        transition: 'opacity 320ms ease-out',
        '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
      }}
    >
      <Button
        onClick={event => {
          // The parent is the skip target, so without this the tap would be handled
          // twice. `finish` is idempotent, but a doubled call is still a wrong thing to
          // rely on.
          event.stopPropagation()
          onClick()
        }}
        variant="contained"
        sx={{
          // Light on black, matching nothing in the brand's theme on purpose: this is
          // Vera's exit from the brand's moment, and borrowing the brand's button colour
          // would make it read as part of the artwork.
          backgroundColor: 'rgba(255,255,255,0.92)',
          color: '#000',
          '&:hover': { backgroundColor: '#fff' },
          px: 4,
        }}
      >
        View certificate
      </Button>

      {/*
        Explicit `sx` rather than `variant="body4"`: the app's custom `body3`/`body4`
        variants exist in the theme at runtime but are NOT in the Typography union
        apps/app's tsconfig resolves, so every use of them is a type error here
        (`EditFieldModal` carries several of the same). Not worth adding one more to that
        pile for a 12px caption on a black overlay, which has bespoke colours anyway.
      */}
      <Typography sx={{ fontSize: 12, lineHeight: 1.4, color: 'rgba(255,255,255,0.6)' }}>
        or tap anywhere
      </Typography>
    </Box>
  )
}
